import {
  EventRecordDto,
  InfantDto,
  VisitDataDto,
  VisitDto,
} from '@ecdlink/core';
import { RootState } from '../types';
import {
  EventRecordType,
  Visit,
  VisitData,
  VisitDataStatus,
} from '@ecdlink/graphql';
import { addMonths } from 'date-fns';
import { isVisitInProgress } from '@/helpers/visit-helpers';
import { createSelector } from '@reduxjs/toolkit';
import { CompletedVisitsForVisitId } from '../visit/visit.types';

const selectInfants = (state: RootState) => state.infants.infants ?? [];

export const getInfants = createSelector([selectInfants], (infant) =>
  infant.filter((infant) =>
    // Check if 'isActive' exists; if it does, use its value; otherwise, return true to include
    'isActive' in infant ? infant.isActive : true
  )
);

export const getInfantsToSync = createSelector([selectInfants], (infants) =>
  infants.filter((item) => 'synced' in item && item.synced === false)
);

export const getInfantVisitsToSync = createSelector(
  [selectInfants],
  (infants) => {
    return infants
      .flatMap((infant) => infant.visits || []) // Extract all visits, safely handling missing visits
      .filter((visit) => 'synced' in visit && visit.synced === false);
  }
);

export const getInfantRecordEventsToSync = createSelector(
  [selectInfants],
  (infants) => {
    return infants
      .flatMap((infant) => infant.eventRecords || [])
      .filter((record) => 'synced' in record && record.synced === false);
  }
);

export const getInfantById = (
  state: RootState,
  id: string
): InfantDto | undefined =>
  state.infants.infants?.find(
    (infant) => infant?.userId === id || infant?.id === id
  );

export const getDashboardInfantVisitsSelector = (
  state: RootState,
  infantId: string = ''
): VisitDto[] => {
  if (infantId) {
    const infant = state.infants.infants?.find((item) => item.id === infantId);
    return infant?.visits || []; // Returns the visits for the specific infant, or an empty array
  }

  // If no infantId is provided, return a flat array of all visits from all infants
  return state.infants.infants?.flatMap((infant) => infant?.visits || []) || [];
};

export const getInfantFirstActiveChildEventSelector = (
  state: RootState,
  infantId: string
): EventRecordDto | null => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return null;

  const events = infant.eventRecords;
  if (!events) return null;

  const monthsAgo = new Date();
  const threeMonthsAgo = new Date(monthsAgo.setMonth(monthsAgo.getMonth() - 3));

  const filteredEvents = events.filter((event) => {
    return (
      event.isActive === true &&
      (event.eventRecordType?.name === 'new_child_in_family' ||
        event.eventRecordType?.name === 'caregiver_is_pregnant')
    );
  });

  const firstEvent = filteredEvents.reduce(
    (oldest: EventRecordDto | null, current: EventRecordDto) => {
      const eventDate = new Date(current.insertedDate!);
      if (
        !oldest ||
        (eventDate.getTime() < new Date(oldest.insertedDate!).getTime() &&
          eventDate.getTime() > threeMonthsAgo.getTime())
      ) {
        return current;
      }
      return oldest;
    },
    null
  );

  return firstEvent;
};

export const getAllInfantFirstActiveChildEventSelector = (
  state: RootState
): EventRecordDto | null => {
  const events =
    state.infants.infants?.flatMap((infant) => infant?.eventRecords || []) ||
    [];

  const monthsAgo = new Date();
  const threeMonthsAgo = new Date(monthsAgo.setMonth(monthsAgo.getMonth() - 3));

  const filteredEvents = events.filter((event) => {
    return (
      event.isActive === true &&
      (event.eventRecordType?.name === 'new_child_in_family' ||
        event.eventRecordType?.name === 'caregiver_is_pregnant')
    );
  });

  const firstEvent = filteredEvents.reduce(
    (oldest: EventRecordDto | null, current: EventRecordDto) => {
      const eventDate = new Date(current.insertedDate!);
      if (
        !oldest ||
        (eventDate.getTime() < new Date(oldest.insertedDate!).getTime() &&
          eventDate.getTime() > threeMonthsAgo.getTime())
      ) {
        return current;
      }
      return oldest;
    },
    null
  );

  return firstEvent;
};

export const getInfantPreviousVisitSelector = (
  state: RootState,
  currentOrderVisitDate: string,
  infantId: string
) => {
  const visits =
    state.infants.visits?.filter((item) => item.clientId === infantId) || [];

  if (!visits) return;

  const filteredVisits = visits.filter((visit) => {
    const orderDate = new Date(visit.orderDate);
    return (
      orderDate < new Date(currentOrderVisitDate) &&
      visit.visitType?.name !== 'additional_visit'
    );
  });

  const previousVisit = filteredVisits.reduce(
    (previous: VisitDto | null, current: VisitDto) => {
      const currentPlannedVisitDate = new Date(current.orderDate);
      if (!previous || currentPlannedVisitDate > new Date(previous.orderDate)) {
        return current;
      }
      return previous;
    },
    null
  );

  return previousVisit;
};

export const getIsInfantFirstVisitSelector = (
  state: RootState,
  infantId: string
): boolean => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return false;

  const visits = infant.visits;
  if (!visits) return false;

  const attendedVisits = visits?.filter(
    (item) => item.visitType?.name !== 'additional_visit' && !!item.attended
  );

  return attendedVisits?.length === 0;
};

export const getIsInfantSecondVisitSelector = (
  state: RootState,
  infantId: string
): boolean => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return false;

  const visits = infant.visits;
  if (!visits) return false;

  const attendedVisits = visits?.filter(
    (item) => item.visitType?.name !== 'additional_visit' && !!item.attended
  );

  return attendedVisits?.length === 1;
};

export const getIsInfantFirstVisitForAgeSelector = (
  state: RootState,
  dob: Date,
  monthsAfter: number,
  monthsBefore: number,
  infantId: string
): boolean => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return false;

  const visits = infant.visits;
  if (!visits) return false;

  const dateAfter = new Date(addMonths(dob, monthsAfter));
  const dateBefore = new Date(addMonths(dob, monthsBefore));

  const attendedVisits = visits?.filter(
    (item) =>
      item.visitType?.name !== 'additional_visit' &&
      !!item.attended &&
      new Date(item.plannedVisitDate).getTime() >= dateAfter.getTime() &&
      new Date(item.plannedVisitDate).getTime() <= dateBefore.getTime()
  );

  return attendedVisits?.length === 0;
};

export const getInfantVisitByVisitIdSelector = (
  state: RootState,
  infantId: string,
  visitId: string
): VisitDto | undefined => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return undefined;

  const visits = infant.visits;
  if (!visits) return undefined;

  return visits?.find((item) => item.id === visitId);
};

export const getAllInfantEventRecordTypesSelector = (
  state: RootState
): EventRecordType[] => state.infants.eventRecordTypes || [];

export const getReferralsForInfantSelector = (
  state: RootState
): VisitDataStatus[] | undefined => state.infants.referralsForInfant || [];

export const getCompletedReferralsForInfantSelector = (
  state: RootState
): VisitDataStatus[] | undefined =>
  state.infants.completedReferralsForInfant || [];

export const getInfantCurrentVisitSelector = (
  state: RootState,
  visitId: string,
  infantId: string
): VisitDto | undefined => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return undefined;

  const visits = infant.visits;
  if (!visits) return undefined;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const allVisits =
    visits?.filter((item) => item.visitType?.name !== 'additional_visits') ||
    [];

  // Priority 1: if a visit id is available, then return visit for id
  if (visitId && visitId !== '') {
    const visits = state.infants.visits || [];
    for (const element of visits) {
      if (element.id === visitId) {
        return element;
      }
    }
  } else {
    // Priority 1.2: if there is a visit completed, we take the last completed one
    const allAttended = allVisits?.filter((item) => item.attended) || [];
    const allAttendedSorted = allAttended.sort(
      (a, b) =>
        new Date(b.actualVisitDate).getTime() -
        new Date(a.actualVisitDate).getTime()
    );
    if (allAttendedSorted.length !== 0) {
      return allAttendedSorted[0];
    }

    // Priority 2: if there is a visit in progress, we grab the first one
    const inProgressList =
      allVisits?.filter((item) => isVisitInProgress(item)) || [];
    if (inProgressList.length !== 0) {
      return inProgressList[0];
    }

    // Priority 3: grab the latest uncompleted visit from the list
    const noAttended =
      allVisits?.filter(
        (item) => !item.attended && new Date(item.orderDate) >= today
      ) || [];
    if (noAttended) {
      if (noAttended.length !== 0) {
        return noAttended[0];
      }
    } else {
      // Priority 4: grab the latest completed visit from the list
      const lastAttended = allVisits?.filter((item) => item.attended) || [];
      return lastAttended.length
        ? lastAttended.reduce((prev, curr) =>
            (prev.visitType?.order || 0) > (curr.visitType?.order || 0)
              ? prev
              : curr
          )
        : undefined;
    }
  }
};

export function getInfantNearestPreviousVisitByOrderDate(
  state: RootState,
  currentVisit?: VisitDto,
  infantId?: string
): VisitDto | undefined {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return undefined;

  const visits = infant.visits;
  if (!visits) return undefined;

  if (currentVisit?.visitType?.name === 'additional_visits') {
    const currentPlannedDate = new Date(currentVisit?.plannedVisitDate!);
    currentPlannedDate?.setHours(23, 59, 59, 0);

    const previousVisits = visits.filter(
      (item) =>
        item.attended &&
        item.actualVisitDate !== null &&
        new Date(item.actualVisitDate) < currentPlannedDate
    );

    if (previousVisits.length === 0) {
      return undefined; // No previous date found
    }

    const nearestDateObject = previousVisits.reduce((previous, current) => {
      if (
        !previous ||
        new Date(currentVisit?.plannedVisitDate!).getTime() -
          new Date(current.actualVisitDate).getTime() <
          new Date(currentVisit?.plannedVisitDate!).getTime() -
            new Date(previous.actualVisitDate).getTime()
      ) {
        return current;
      }
      return previous;
    });

    return nearestDateObject;
  }

  const allAttended = visits?.filter((item) => item.attended) || [];
  const previousVisits = allAttended.sort(
    (a, b) =>
      new Date(b.actualVisitDate).getTime() -
      new Date(a.actualVisitDate).getTime()
  );

  if (previousVisits.length !== 0) {
    return previousVisits[0];
  } else {
    return undefined;
  }
}

//-----------------------------------------------------------------
// new selectors reading data from state in stead of back-end calls
//-----------------------------------------------------------------

export const getVisitAnswersForInfantSelector = (
  state: RootState,
  clientId: string,
  visitId: string = '',
  visitName: string = '',
  visitSection: string = ''
): VisitDataDto[] => {
  const infant = state.infants.infants?.find(
    (m) => m.id === clientId || m.userId === clientId
  );
  if (!infant) return [];

  const visits = infant.visits;
  if (!visits) return [];

  let visit: VisitDto;

  if (visitId !== '') {
    visit = visits?.find((v) => v.id === visitId)!;
  } else {
    const allAttended = visits?.filter((item) => item.attended) || [];
    const previousVisits = allAttended.sort(
      (a, b) =>
        new Date(b.actualVisitDate).getTime() -
        new Date(a.actualVisitDate).getTime()
    );
    visit = previousVisits[0];
  }

  if (visitName === '' && visitSection === '') {
    return (visit?.visitAnswers as VisitDataDto[]) || [];
  }

  if (visitName !== '' && visitSection === '') {
    return visit.visitAnswers?.filter((a) => a.visitName === visitName) ?? [];
  }

  if (visitName !== '' && visitSection !== '') {
    return (
      visit.visitAnswers?.filter(
        (a) => a.visitName === visitName && a.visitSection === visitSection
      ) ?? []
    );
  }

  return [];
};

export const getInfantCompletedVisitsByVisitIdSelector = (
  state: RootState,
  infantId: string,
  visitId: string
): CompletedVisitsForVisitId => {
  const infant = state.infants.infants?.find(
    (i) => i.id === infantId || i.userId === infantId
  );

  const visit = infant?.visits?.find((v) => v.id === visitId);

  const uniqueVisitNames = Array.from(
    new Set(
      visit?.visitAnswers
        ?.map((a) => a.visitName)
        ?.filter((name): name is string => name !== undefined) ?? []
    )
  );

  return {
    visitId,
    visits: uniqueVisitNames,
  };
};

export const getInfantsByCaregiverIdSelector = (
  state: RootState,
  caregiverId: string
): InfantDto[] | [] => {
  return (
    state.infants.infants?.filter((i) => i.caregiver?.id === caregiverId) || []
  );
};

export const getGrowthDataForInfantSelector = (
  state: RootState,
  infantId: string
): VisitData[] => {
  const infant = state.infants.infants?.find(
    (m) => m.id === infantId || m.userId === infantId
  );
  if (!infant) return [];

  const visits = infant.visits ?? [];
  if (visits.length === 0) return [];

  const growthQuestions = new Set([
    'Weight',
    'Length',
    'What is {client} MUAC today?',
  ]);

  const allVisitData: VisitData[] = [];

  for (const visit of visits) {
    const answers = visit.visitAnswers ?? [];
    for (const answer of answers) {
      const q = answer?.question?.trim();
      if (q && growthQuestions.has(q)) {
        allVisitData.push({
          id: answer.id,
          question: answer.question,
          questionAnswer: answer.questionAnswer,
          visit: visit as Visit,
          visitId: visit.id,
          visitName: answer.visitName,
          visitSection: answer.visitSection,
          insertedDate: undefined,
          isActive: true,
          updatedDate: undefined,
        });
      }
    }
  }

  return allVisitData;
};
